// The asset page's Certificates card (CR_20260921_Asset_Lifecycle T1, §7.1): what the category requires and what the
// asset holds, each with its state today. It renders only when there is something to show — a required kind or a
// certificate on file — because an empty card reads as "nothing to do here". A required, blocking kind with nothing on
// file says so in words: the asset cannot be dispatched. Managers add, replace and delete (§4.5); everyone who can see
// the asset can open the file. History (when it was added) lives in the Lifecycle tab, not here.
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type CertState,
  type CertStatusRow,
  DOC_TYPES,
  docProblem,
  expiryFrom,
  sortCertRows,
} from '@fleetspoke/core/lifecycle';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  openDocument,
  useAssetCertStatus,
  useCertKinds,
  useDeleteCertificate,
  useSaveCertificate,
} from '../../data/certificates';
import { calendarDate } from '../../lib/time';
import { Card, CardHeader } from './Card';

const STATE_COLOR: Record<CertState, 'success' | 'warning' | 'error' | 'default'> = {
  valid: 'success',
  expiring: 'warning',
  expired: 'error',
  missing: 'error',
};

export function CertStateChip({ state }: { state: CertState }) {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      label={t(`certs.state_${state}`)}
      color={STATE_COLOR[state]}
      variant={state === 'valid' ? 'outlined' : 'filled'}
      data-testid={`cert-state-${state}`}
    />
  );
}

function when(
  r: CertStatusRow,
  language: string,
  t: (k: string, o?: Record<string, unknown>) => string,
) {
  if (!r.expires_on) return null;
  const date = calendarDate(r.expires_on, language) ?? r.expires_on;
  if (r.state === 'expired') return t('certs.expired_on', { date });
  const left =
    r.days_left != null && r.state === 'expiring'
      ? ` · ${t('certs.left', { count: r.days_left })}`
      : '';
  return `${t('certs.valid_until', { date })}${left}`;
}

export function CertificatesCard({
  assetId,
  companyId,
  canEdit,
  adding,
  onAddingDone,
}: {
  assetId: string;
  companyId: string;
  canEdit: boolean;
  /** opened from the page's ⋮ menu (an asset with nothing required and nothing on file has no card) */
  adding?: boolean;
  onAddingDone?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const rows = useAssetCertStatus(assetId);
  const [editing, setEditing] = useState<CertStatusRow | 'new' | null>(null);
  const [deleting, setDeleting] = useState<CertStatusRow | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const list = sortCertRows(rows.data ?? []);
  const dialog =
    editing || adding ? (
      <CertificateDialog
        assetId={assetId}
        companyId={companyId}
        row={editing && editing !== 'new' ? editing : undefined}
        onClose={() => {
          setEditing(null);
          onAddingDone?.();
        }}
      />
    ) : null;
  if (rows.isLoading || !list.length) return dialog;

  const open = (path: string) => {
    setOpenError(null);
    openDocument(path).catch((e: Error) => setOpenError(e.message));
  };

  return (
    <Card data-testid="asset-certificates">
      <CardHeader
        title={t('certs.title')}
        aside={
          canEdit ? (
            <Button size="small" onClick={() => setEditing('new')} data-testid="cert-add">
              {t('certs.add')}
            </Button>
          ) : null
        }
      />
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} spacing={1.25}>
        {list.map((r) => (
          <Stack
            key={r.certificate_id ?? `req-${r.kind}`}
            direction="row"
            spacing={1.5}
            alignItems="center"
            data-testid="cert-row"
          >
            <CertStateChip state={r.state} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {r.kind}
                {r.number ? (
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                    {' · '}
                    {r.number}
                  </Box>
                ) : null}
                {r.required ? (
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                    {' · '}
                    {t('certs.required')}
                  </Box>
                ) : null}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {r.state === 'missing'
                  ? t(r.blocks_dispatch ? 'certs.missing_blocks' : 'certs.missing_required', {
                      kind: r.kind,
                    })
                  : [
                      when(r, i18n.language, t),
                      r.issuer,
                      r.state === 'expired' && r.blocks_dispatch ? t('certs.expired_blocks') : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
              </Typography>
            </Box>
            {r.document_path ? (
              <Tooltip title={t('certs.open')}>
                <IconButton
                  size="small"
                  onClick={() => open(r.document_path!)}
                  aria-label={t('certs.open')}
                >
                  <PictureAsPdfOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {canEdit && r.certificate_id ? (
              <>
                <Tooltip title={t('certs.edit')}>
                  <IconButton
                    size="small"
                    onClick={() => setEditing(r)}
                    aria-label={t('certs.edit')}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('certs.delete')}>
                  <IconButton
                    size="small"
                    onClick={() => setDeleting(r)}
                    aria-label={t('certs.delete')}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : canEdit && r.state === 'missing' ? (
              <Button size="small" onClick={() => setEditing({ ...r })}>
                {t('certs.add')}
              </Button>
            ) : null}
          </Stack>
        ))}
      </Stack>
      {openError ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {t('common.error_generic')} {openError}
        </Alert>
      ) : null}
      {dialog}
      {deleting ? (
        <DeleteCertificateDialog
          assetId={assetId}
          row={deleting}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </Card>
  );
}

function DeleteCertificateDialog({
  assetId,
  row,
  onClose,
}: {
  assetId: string;
  row: CertStatusRow;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const del = useDeleteCertificate(assetId);
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('certs.delete_title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('certs.delete_body', { kind: row.kind, number: row.number ?? '' })}
        </DialogContentText>
        {del.error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('common.error_generic')} {(del.error as Error).message}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          color="error"
          variant="contained"
          disabled={del.isPending}
          onClick={() =>
            del.mutate(
              { id: row.certificate_id!, document_path: row.document_path },
              { onSuccess: onClose },
            )
          }
        >
          {t('certs.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Add a certificate, or edit one (`row` with a certificate_id). A row without a certificate (a required kind that is
 * missing) opens the dialog with its kind filled in and the expiry prefilled from the rule's "valid for".
 */
export function CertificateDialog({
  assetId,
  companyId,
  row,
  onClose,
}: {
  assetId: string;
  companyId: string;
  row?: CertStatusRow;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const save = useSaveCertificate(companyId, assetId);
  const kinds = useCertKinds();
  const existing = row?.certificate_id ? row : undefined;
  const [kind, setKind] = useState(row?.kind ?? '');
  const [number, setNumber] = useState(existing?.number ?? '');
  const [issuer, setIssuer] = useState(existing?.issuer ?? '');
  const [issuedOn, setIssuedOn] = useState(existing?.issued_on ?? '');
  const [expiresOn, setExpiresOn] = useState(existing?.expires_on ?? '');
  const [note, setNote] = useState(existing?.note ?? '');
  const [file, setFile] = useState<File | null>(null);
  const problem = file ? docProblem(file) : null;

  const onIssued = (v: string) => {
    setIssuedOn(v);
    // prefill the expiry from the rule, only while the user has not set one
    const prefill = expiryFrom(v, row?.valid_for_days);
    if (prefill && !expiresOn) setExpiresOn(prefill);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      {
        id: existing?.certificate_id ?? undefined,
        kind,
        number,
        issuer,
        issued_on: issuedOn,
        expires_on: expiresOn,
        note,
        file,
        document_path: existing?.document_path ?? null,
      },
      { onSuccess: onClose },
    );
  };
  const valid = kind.trim() && expiresOn && !problem && (!issuedOn || expiresOn >= issuedOn);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>{existing ? t('certs.edit') : t('certs.add')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Autocomplete
              freeSolo
              options={kinds.data ?? []}
              value={kind}
              onInputChange={(_, v) => setKind(v)}
              disabled={!!row && !!row.required}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label={t('certs.kind')}
                  helperText={t('certs.kind_help')}
                  required
                  slotProps={{ htmlInput: { ...p.inputProps, maxLength: 40 } }}
                />
              )}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('certs.number')}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                fullWidth
                slotProps={{ htmlInput: { maxLength: 80 } }}
              />
              <TextField
                label={t('certs.issuer')}
                value={issuer}
                onChange={(e) => setIssuer(e.target.value)}
                fullWidth
                slotProps={{ htmlInput: { maxLength: 120 } }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                type="date"
                label={t('certs.issued_on')}
                value={issuedOn}
                onChange={(e) => onIssued(e.target.value)}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                type="date"
                label={t('certs.expires_on')}
                value={expiresOn}
                onChange={(e) => setExpiresOn(e.target.value)}
                required
                fullWidth
                error={!!issuedOn && !!expiresOn && expiresOn < issuedOn}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { 'data-testid': 'cert-expires' },
                }}
              />
            </Stack>
            <Box>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                {t('certs.file')} · {t('certs.file_hint')}
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button component="label" variant="outlined" size="small">
                  {t('certs.file_pick')}
                  <input
                    hidden
                    type="file"
                    accept={DOC_TYPES.join(',')}
                    data-testid="cert-file"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                </Button>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                  {file?.name ??
                    (existing?.document_path ? t('certs.file_keep') : t('certs.no_file'))}
                </Typography>
              </Stack>
              {problem ? (
                <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>
                  {t(problem === 'too_big' ? 'certs.file_too_big' : 'certs.file_type')}
                </Typography>
              ) : null}
            </Box>
            <TextField
              label={t('certs.note')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
            {save.error ? (
              <Alert severity="error">
                {t('common.error_generic')} {(save.error as Error).message}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!valid || save.isPending}
            data-testid="cert-save"
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
